import { motion } from 'framer-motion';
import JSConfetti from 'js-confetti';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
	Avatar,
	Box,
	Button,
	ClappingHandsSolid,
	Divider,
	FormControl,
	FormLabel,
	HStack,
	IconButton,
	Select,
	Text,
	useBreakpointValue,
	useColorModeValue,
	useToast,
	VStack,
	Wrap,
	XOutline,
} from '@bonusly/design-system';
import { ProductTier, useCurrentCompany } from '@bonusly/who-am-i';

import { GiveModal } from '../../../../components/recognition/give-box/components/give-box';
import { Goal } from '../../../../types/goal';
import { useInitiatives } from '../../../admin-growth/hooks/use-initiatives';
import { useHashtags } from '../../../bonus-feed/features/recognition/api/hooks/hashtags';
import { useUpdateGoal } from '../../hooks/use-goals';
import { GoalStatusBadge } from './goal-status-badge';

const MotionBox = motion.create(Box);

interface FormData {
	initiativeId: string;
	tags: string[];
}

interface GoalCompletedModalProps {
	isOpen: boolean;
	onClose: () => void;
	goal: Goal | null;
	onSuccess?: () => void;
}

export const GoalCompletedModal: FC<GoalCompletedModalProps> = ({ isOpen, onClose, goal, onSuccess }) => {
	const toast = useToast();
	const company = useCurrentCompany();
	const bgColor = useColorModeValue('bg.surface', 'bg.canvas');
	const borderColor = useColorModeValue('border.default', 'border.default');
	const showButtonIcon = useBreakpointValue({ base: false, md: true });
	const updateGoal = useUpdateGoal();
	const initiativeId = goal?.initiative_id || (goal as any).initiativeId || goal?.initiative?.id || '';

	const isOrganizationTier = company.product_tier === ProductTier.TierThree;
	const { data: initiatives = [] } = useInitiatives(isOrganizationTier);

	// Get company values aka hashtags
	const { data: hashtags } = useHashtags({
		params: {
			display_format: 'index',
		},
	});

	const {
		control,
		handleSubmit,
		reset,
		formState: { isSubmitting },
	} = useForm<FormData>({
		defaultValues: {
			initiativeId: initiativeId,
			tags: Array.isArray(goal?.tags) ? goal.tags : [],
		},
	});

	// State for tracking recognition and modal transitions
	const [recognizedCollaborators, setRecognizedCollaborators] = useState<Set<string>>(new Set());
	const [showGiveModal, setShowGiveModal] = useState(false);
	const [selectedCollaborator, setSelectedCollaborator] = useState<any>(null);

	// 🎉 CELEBRATION CONFETTI! 🎉
	useEffect(() => {
		if (isOpen && goal) {
			const timer = setTimeout(() => {
				const jsConfetti = new JSConfetti();

				// First wave - emoji celebration!
				jsConfetti.addConfetti({
					emojis: ['🎉', '🎊', '⭐', '🏆', '🎯', '💪', '🚀', '✨', '🌟', '🎈'],
					emojiSize: 100,
					confettiNumber: 50,
				});

				// Second wave - more traditional confetti colors
				setTimeout(() => {
					jsConfetti.addConfetti({
						confettiColors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF7F50', '#98D8C8', '#F7DC6F'],
						confettiNumber: 100,
					});
				}, 200);

				// Third wave - sparkle effect!
				setTimeout(() => {
					jsConfetti.addConfetti({
						emojis: ['✨', '⭐', '🌟', '💫'],
						emojiSize: 80,
						confettiNumber: 30,
					});
				}, 400);
			}, 500);

			return () => clearTimeout(timer);
		}
	}, [isOpen, goal]);

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setRecognizedCollaborators(new Set());
			setShowGiveModal(false);
			setSelectedCollaborator(null);
		}
	}, [isOpen]);

	// Update form when goal changes
	useEffect(() => {
		if (goal) {
			reset({
				initiativeId: initiativeId,
				tags: Array.isArray(goal.tags) ? goal.tags : [],
			});
		}
	}, [goal, reset, initiativeId]);

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleSkip = () => {
		handleClose();
	};

	const onSubmit = async (data: FormData) => {
		if (!goal) return;

		try {
			// Check if we need to update the goal (initiative or tags changed)
			const currentInitiativeId = goal.initiative_id || (goal as any).initiativeId || goal.initiative?.id || '';
			const currentTags = Array.isArray(goal.tags) ? goal.tags : [];

			const initiativeChanged = data.initiativeId !== currentInitiativeId;
			const tagsChanged = JSON.stringify(data.tags.sort()) !== JSON.stringify(currentTags.sort());

			if (initiativeChanged || tagsChanged) {
				// Update the goal with new initiative and/or tags
				await updateGoal.mutateAsync({
					id: goal.id,
					data: {
						initiativeId: data.initiativeId || undefined,
						tags: data.tags,
					},
				});
			}

			toast({
				title: 'Goal completion recorded! 🎉',
				description: 'Your achievement has been celebrated.',
				status: 'success',
				duration: 4000,
			});

			onSuccess?.();
			handleClose();
		} catch (error) {
			console.error('Failed to update goal:', error);
			toast({
				title: 'Failed to update goal',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	const handleGiveRecognition = (collaborator: any) => {
		// Convert collaborator to the format expected by GiveBoxEvent
		const recipient = {
			id: collaborator.id,
			displayName: collaborator.displayName,
			profilePic: collaborator.fullPicUrl || collaborator.profilePicUrl,
			username: collaborator.username,
		};

		setSelectedCollaborator(recipient);
		setShowGiveModal(true);
	};

	const handleGiveModalClose = () => {
		setShowGiveModal(false);
		setSelectedCollaborator(null);
	};

	const handleGiveModalSuccess = () => {
		// Mark this collaborator as having received recognition
		if (selectedCollaborator) {
			setRecognizedCollaborators((prev) => new Set(prev).add(selectedCollaborator.id));
		}

		toast({
			title: 'Recognition sent! 🎉',
			description: `You've recognized ${selectedCollaborator?.displayName} for their collaboration.`,
			status: 'success',
			duration: 4000,
		});

		// Return to the GoalCompletedModal to allow recognizing other collaborators
		handleGiveModalClose();
	};

	if (!goal) return null;

	const collaborators = goal.collaborators || [];
	const hasCollaborators = collaborators.length > 0;

	return (
		<>
			{/* Backdrop overlay - stays in place, fades out when GiveModal opens */}
			{isOpen && (
				<MotionBox
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					bg="blackAlpha.600"
					backdropFilter="blur(4px)"
					zIndex={1500}
					initial={{ opacity: 0 }}
					animate={{ opacity: showGiveModal ? 0 : 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
					pointerEvents={showGiveModal ? 'none' : 'auto'}
					onClick={handleClose}
				/>
			)}

			{/* Goal Completed Modal content - slides left */}
			{isOpen && (
				<MotionBox
					position="fixed"
					top={0}
					left={0}
					right={0}
					bottom={0}
					display="flex"
					alignItems={{ base: 'stretch', md: 'center' }}
					justifyContent={{ base: 'stretch', md: 'center' }}
					zIndex={1501}
					animate={{
						x: showGiveModal ? '-100%' : '0%',
					}}
					transition={{
						duration: 0.4,
						ease: 'easeInOut',
						type: 'tween',
					}}
					pointerEvents={showGiveModal ? 'none' : 'auto'}
				>
					<Box
						bg={bgColor}
						borderRadius={{ base: '0', md: 'xl' }}
						border={{ base: 'none', md: '1px solid' }}
						borderColor={borderColor}
						maxW={{ base: 'none', md: '500px' }}
						width={{ base: '100vw', md: '90vw' }}
						height={{ base: '100vh', md: 'auto' }}
						maxH={{ base: '100vh', md: '90vh' }}
						overflowY="auto"
						boxShadow={{ base: 'none', md: '2xl' }}
						pt={{ base: '64px', md: '0' }}
					>
						<form
							onSubmit={handleSubmit(onSubmit)}
							style={{
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
							}}
						>
							{/* Modal Header */}
							<Box
								pb={3}
								pt={{ base: 4, md: 6 }}
								px={{ base: 4, md: 6 }}
								borderBottom={{ base: '1px solid', md: 'none' }}
								borderColor="border.default"
								bg={bgColor}
								position={{ base: 'sticky', md: 'relative' }}
								top={{ base: 0, md: 'auto' }}
								zIndex={1}
							>
								<HStack spacing={3} align="center">
									<Text fontSize="xl" fontWeight="semibold">
										Goal completed!
									</Text>
									<Text fontSize="2xl">🎉</Text>
								</HStack>
								<IconButton
									aria-label="Close modal"
									icon={<XOutline />}
									variant="ghost"
									size="sm"
									position="absolute"
									top={{ base: 2, md: 4 }}
									right={{ base: 2, md: 4 }}
									onClick={handleClose}
								/>
							</Box>

							{/* Modal Body */}
							<Box py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }} flex={{ base: 1, md: 'none' }} overflowY={{ base: 'auto', md: 'visible' }}>
								<VStack spacing={6} align="stretch">
									{/* Green Section with Goal Info */}
									<Box bg="points.bgAlpha" borderRadius="xl" p={4} border="1px solid" borderColor="border.soft">
										<VStack align="start" spacing={4}>
											{/* Completed Status Badge */}
											<GoalStatusBadge status="completed" />

											{/* Goal Title */}
											<Text fontSize="xl" fontWeight="semibold" lineHeight="shorter" color="text.default" ml={1}>
												{goal.title}
											</Text>

											{/* Goal Details */}
											{goal.summary && (
												<Text color="text.softer" fontSize="md" lineHeight="relaxed" ml={1}>
													{goal.summary}
												</Text>
											)}
										</VStack>
									</Box>

									{/* Connect Achievement Text */}
									<Text color="text.soft" fontSize="md">
										Connect your achievement and celebrate your team.
									</Text>

									{/* Initiative Selection - Only show for Organization tier */}
									{isOrganizationTier && (
										<FormControl>
											<FormLabel fontSize="sm" fontWeight="medium" color="text.soft">
												Which initiative does this support?
											</FormLabel>
											<Controller
												name="initiativeId"
												control={control}
												render={({ field }) => (
													<Select
														{...field}
														placeholder="Select an initiative..."
														borderRadius="md"
														border="1px solid"
														borderColor="border.default"
														_focus={{ borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' }}
													>
														<option key="none" value="">
															None
														</option>
														{initiatives.map((initiative) => (
															<option key={initiative.id} value={initiative.id}>
																{initiative.title}
															</option>
														))}
													</Select>
												)}
											/>
										</FormControl>
									)}

									{/* Company Values */}
									<FormControl>
										<FormLabel fontSize="sm" fontWeight="medium" color="text.soft">
											Which company values does this reflect?
										</FormLabel>
										<Controller
											name="tags"
											control={control}
											render={({ field }) => (
												<Wrap>
													{hashtags?.data?.result?.map((hashtag: string) => {
														const isSelected = field.value.includes(hashtag);
														return (
															<Button
																key={hashtag}
																variant={isSelected ? 'solid' : 'outline'}
																colorScheme={isSelected ? 'hashtag' : 'border'}
																onClick={() => {
																	if (isSelected) {
																		field.onChange(field.value.filter((h: string) => h !== hashtag));
																	} else {
																		field.onChange([...field.value, hashtag]);
																	}
																}}
																borderRadius="full"
																borderColor="grayAlpha.100/24"
																size="xs"
															>
																{hashtag}
															</Button>
														);
													})}
												</Wrap>
											)}
										/>
										<Text fontSize="xs" color="text.softest" mt={2}>
											Click to select/deselect
										</Text>
									</FormControl>

									{/* Collaborators Section */}
									{hasCollaborators && (
										<>
											<Divider />
											<VStack align="start" spacing={4}>
												<Text fontSize="sm" fontWeight="medium" color="text.soft">
													Celebrate your collaborators
												</Text>
												<VStack spacing={3} align="stretch" w="full">
													{collaborators.map((collaborator) => {
														const hasBeenRecognized = recognizedCollaborators.has(collaborator.id);

														return (
															<HStack
																key={collaborator.id}
																spacing={3}
																p={3}
																borderRadius="md"
																border="1px solid"
																borderColor="border.default"
																bg="bg.subtle"
																justify="space-between"
															>
																<HStack spacing={3}>
																	<Avatar src={collaborator.fullPicUrl || collaborator.profilePicUrl || undefined} name={collaborator.displayName} size="sm" />
																	<VStack align="start" spacing={0}>
																		<Text fontSize="sm" fontWeight="medium" color="text.default">
																			{collaborator.displayName}
																		</Text>
																		<Text fontSize="xs" color="text.softest">
																			@{collaborator.username}
																		</Text>
																	</VStack>
																</HStack>

																{hasBeenRecognized ? (
																	<HStack spacing={2}>
																		<Text fontSize="xs" color="success.text" fontWeight="medium">
																			Recognition sent
																		</Text>
																		<Text color="success.icon">✓</Text>
																	</HStack>
																) : (
																	<Button
																		size="sm"
																		variant="outline"
																		colorScheme="primary"
																		leftIcon={showButtonIcon ? <ClappingHandsSolid size="xs" /> : undefined}
																		onClick={() => handleGiveRecognition(collaborator)}
																		fontSize="xs"
																	>
																		Give Recognition
																	</Button>
																)}
															</HStack>
														);
													})}
												</VStack>
											</VStack>
										</>
									)}
								</VStack>
							</Box>

							{/* Modal Footer */}
							<Box
								px={{ base: 4, md: 6 }}
								py={{ base: 4, md: 4 }}
								borderTop="1px solid"
								borderColor={borderColor}
								bg={bgColor}
								position={{ base: 'sticky', md: 'static' }}
								bottom={{ base: 0, md: 'auto' }}
								zIndex={1}
								mt={{ base: 'auto', md: 0 }}
							>
								<HStack spacing={3}>
									<Button variant="ghost" onClick={handleSkip} color="text.softer" flex={{ base: 1, md: 'none' }}>
										Skip for now
									</Button>
									<Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText="Saving changes..." flex={{ base: 1, md: 'none' }}>
										Save changes
									</Button>
								</HStack>
							</Box>
						</form>
					</Box>
				</MotionBox>
			)}

			{/* Give Recognition Modal - Standard Chakra Modal */}
			<GiveModal
				isOpen={showGiveModal}
				onClose={handleGiveModalClose}
				onSuccess={handleGiveModalSuccess}
				content={
					selectedCollaborator
						? {
								type: 'doc',
								content: [
									{
										type: 'paragraph',
										content: [
											{
												type: 'recipient',
												attrs: {
													id: selectedCollaborator.username,
													record: {
														id: selectedCollaborator.id,
														displayName: selectedCollaborator.displayName,
														profilePic: selectedCollaborator.profilePic,
														username: selectedCollaborator.username,
													},
												},
											},
											{
												type: 'text',
												text: ' ',
											},
										],
									},
								],
							}
						: undefined
				}
				via="goal-completion"
			/>
		</>
	);
};
